// MenopauseRiskScreening.jsx
// Production-ready Menopause Risk Screening
// Uses: ScreeningLayout.jsx (shared shell) + menopauseScreeningData.js (JSON config)
import React, { useRef, useState } from 'react';
import {
  AppShell, Card, CardHeader, CardBody, CardFooter,
  ProgressBar, Sidebar,
  Logo, ChevronRight, Check, ReviewRow,
  ToggleGroup, ConcernGrid,
  ResultGauge, ConfidenceBar, NextActionList,
} from './ScreeningLayout';
import styles from './screening.module.css';
import {
  MENOPAUSE_STEPS, MENOPAUSE_FLOW,
  MENOPAUSE_INTRO, MENOPAUSE_CONSENT, MENOPAUSE_CONCERNS,
  MENOPAUSE_QUESTION_SCREENS, MENOPAUSE_REVIEW_SECTIONS,
  computeMenopauseScore, getMenopauseRisk,
} from './menopauseScreeningData';
// import { ConsentScreen } from '../../pages/CareNavigator';
import PregnancyWelcomeWeb from '../PregnancyRiskScreening/PregWelcomeScreen';
import ConsentScreen from '../Reusablecomponents/Consentscreen/Consentscreen';
import { RISK_SCREENING_CONSENT } from './MentalWellnessScreening';
// import ConsentScreen from '../CareJourney/Consentscreen';

// ── Illustration ────────────────────────────────────────────────────────────
function MenopauseIllustration() {
  return (
    <svg viewBox="0 0 260 280" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.illusSvg}>
      {/* Background blobs */}
      <circle cx="150" cy="130" r="100" fill="#FCE7F3" opacity=".55" />
      <ellipse cx="130" cy="200" rx="80" ry="30" fill="#FBCFE8" opacity=".35" />
      {/* Sofa */}
      <rect x="50" y="195" width="160" height="35" rx="12" fill="#F48FB1" opacity=".7" />
      <rect x="42" y="182" width="22" height="48" rx="9" fill="#EC407A" opacity=".8" />
      <rect x="196" y="182" width="22" height="48" rx="9" fill="#EC407A" opacity=".8" />
      <rect x="50" y="177" width="160" height="22" rx="9" fill="#F06292" opacity=".75" />
      {/* Plant */}
      <rect x="28" y="210" width="14" height="14" rx="2" fill="#F48FB1" />
      <path d="M35 210Q30 195 34 180" stroke="#66BB6A" strokeWidth="2" fill="none" />
      <ellipse cx="26" cy="192" rx="9" ry="6" fill="#A5D6A7" transform="rotate(-20 26 192)" />
      <ellipse cx="38" cy="184" rx="8" ry="5" fill="#C8E6C9" transform="rotate(15 38 184)" />
      {/* Figure */}
      <circle cx="130" cy="105" r="22" fill="#F48FB1" />
      {/* Hair */}
      <path d="M112 98Q118 76 142 80Q154 90 148 106" fill="#AD1457" />
      {/* Body */}
      <path d="M108 175Q114 148 130 145Q146 148 152 175" fill="#E91E63" opacity=".85" />
      {/* Legs */}
      <path d="M108 175Q100 188 122 192Q140 192 162 175" fill="#F48FB1" opacity=".9" />
      {/* Arm + cup */}
      <path d="M112 155Q92 162 86 174" stroke="#F48FB1" strokeWidth="6" strokeLinecap="round" fill="none" />
      <rect x="72" y="170" width="16" height="14" rx="4" fill="#FCE4EC" stroke="#F48FB1" strokeWidth="1.5" />
      <path d="M76 168Q77 162 76 157" stroke="#F48FB1" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity=".6" />
      <path d="M82 168Q83 161 82 156" stroke="#F48FB1" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity=".6" />
      {/* Heart */}
      <path d="M130 78C130 78 126 72 121 76C117 80 121 86 130 92C139 86 143 80 139 76C134 72 130 78 130 78Z" fill="#E91E63" opacity=".85" />
      {/* Hot flush wavy lines */}
      <path d="M158 90Q165 84 162 78Q159 72 165 66" stroke="#FCA5A5" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M165 95Q172 89 169 83Q166 77 172 71" stroke="#FECACA" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function ConsentIllustration() {
  return (
    <svg viewBox="0 0 80 80" fill="none" className={styles.consentIllusSvg}>
      <rect x="15" y="10" width="50" height="60" rx="6" fill="#FCE7F3" stroke="#BE185D" strokeWidth="1.5" />
      <rect x="22" y="22" width="36" height="4" rx="2" fill="#BE185D" opacity=".4" />
      <rect x="22" y="32" width="28" height="4" rx="2" fill="#BE185D" opacity=".3" />
      <rect x="22" y="42" width="32" height="4" rx="2" fill="#BE185D" opacity=".3" />
      <circle cx="58" cy="58" r="14" fill="#BE185D" />
      <path d="M52 58L56 62L64 54" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SUB-SCREENS (identical pattern to PCOS — consistent UX)
// ══════════════════════════════════════════════════════════════════════════════

function IntroScreen({ config, onStart }) {
  return (
    <CardBody>
      <div className={styles.introWrap}>
        <div className={styles.introContent}>
          <span className={styles.introBadge}>
            <span>{config.badgeIcon}</span> {config.badge}
          </span>
          <h1 className={styles.introHeading}>{config.heading}</h1>
          <p className={styles.introDesc}>{config.desc}</p>
          <ul className={styles.introBullets}>
            {config.bullets.map((b) => (
              <li key={b.text} className={styles.introBullet}>
                <span className={styles.introBulletIcon}>{b.icon}</span>
                <span>{b.text}</span>
              </li>
            ))}
          </ul>
          <div className={styles.introPrivacy}>🛡️ {config.privacy}</div>
          <button className={styles.introStartBtn} onClick={onStart}>
            {config.startLabel} <ChevronRight />
          </button>
          <button className={styles.introLaterBtn}>{config.laterLabel}</button>
        </div>
        <div className={styles.introIllus}>
          <div className={styles.illusBlob} />
          <MenopauseIllustration />
        </div>
      </div>
    </CardBody>
  );
}

// function ConsentScreen({ config, onAgree, onBack }) {
//   const [checked, setChecked] = useState(false);
//   return (
//     <CardBody>
//       <div className={styles.consentWrap}>
//         <div className={styles.consentIllusWrap}><ConsentIllustration /></div>
//         <h2 className={styles.consentHeading}>{config.heading}</h2>
//         {config.paragraphs.map((p, i) => (
//           <p key={i} className={styles.consentText}>{p}</p>
//         ))}
//         <button
//           className={`${styles.consentCheckBtn} ${checked ? styles.consentCheckBtnActive : ''}`}
//           onClick={() => setChecked((v) => !v)}
//         >
//           <span className={styles.consentBox}>{checked && <Check />}</span>
//           <span>{config.checkLabel}</span>
//         </button>
//         <button className={styles.consentAgreeBtn} disabled={!checked} onClick={onAgree}>
//           {config.agreeLabel} <ChevronRight />
//         </button>
//       </div>
//     </CardBody>
//   );
// }

function ConcernsScreen({ config, selected, onChange, onNext, onBack }) {
  return (
    <>
      <div className={styles.cardBodyConsent}>
        <div className={styles.screenStepBadge}>
          <span className={styles.screenStepNum}>1</span> Main Concerns
        </div>
        <h2 className={`ava-text-subheading ${styles.screenTitle}`}>What are your main concerns?</h2>
        <p className={styles.screenSubtitle}>Select all that apply</p>
        <ConcernGrid options={config} selected={selected} onChange={onChange} />
      </div>
      <CardFooter onBack={onBack} onNext={onNext} nextLabel="Continue" disabled={selected.length === 0} />
    </>
  );
}

function QuestionScreen({ screenConfig, answers, onChange, onNext, onBack, questionIndex, totalQuestions,qIdxMap }) {
  const screenId = screenConfig.id;

  // const [qIdx, setQIdx] = useState(0);
  const [qIdx, setQIdx] = useState(
    () => qIdxMap?.current?.[screenId] ?? 0
  );

 const updateQIdx = (newIdx) => {
    setQIdx(newIdx);
    if (qIdxMap?.current) qIdxMap.current[screenId] = newIdx;
  };
  const visible = screenConfig.questions.filter((q) => {
    if (!q.conditional) return true;
    const { field, notValue, value } = q.conditional;
    const fv = answers[field];
    if (value !== undefined) return fv === value;
    if (notValue !== undefined) return fv !== notValue;
    return true;
  });

  const current = visible[qIdx];
  const isLast = qIdx === visible.length - 1;

  // const handleNext = () => { if (!isLast) { setQIdx((i) => i + 1); return; } onNext(); };
  // const handleBack = () => { if (qIdx > 0) { setQIdx((i) => i - 1); return; } onBack(); };
  const handleNext = () => { if (!isLast) { updateQIdx(qIdx + 1); return; } onNext(); };
  const handleBack = () => { if (qIdx > 0) { updateQIdx(qIdx - 1); return; } onBack(); };

  return (
    <>
      <ProgressBar
        // step={questionIndex + 1}
        // total={totalQuestions}
        // label={screenConfig.stepLabel}
        step={qIdx}
        total={visible.length}
        label={screenConfig.stepLabel}
      />
      <CardBody>
        <div className={styles.screenStepBadge}>
          <span className={styles.screenStepNum}>{screenConfig.icon}</span>
          {screenConfig.stepLabel}
        </div>
        <div className={styles.questionCounter}>
          <span className={styles.questionCountCurrent}>Question: {qIdx + 1}</span>
          <span className={styles.questionCountSep}>/</span>
          <span className={styles.questionCountTotal}>{visible.length}</span>
        </div>
        {/* <h2 className={`ava-text-subheading ${styles.screenTitle}`}>{screenConfig.title}</h2> */}
        {qIdx === 0 && <p className={styles.screenSubtitle}>{screenConfig.subtitle}</p>}
        {current && (
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>{current.label}</label>
            <ToggleGroup
              options={current.options}
              value={answers[current.id]}
              onChange={(v) => onChange(current.id, v)}
            />
          </div>
        )}
      </CardBody>
      <CardFooter
        onBack={handleBack}
        onNext={handleNext}
        nextLabel={isLast ? 'Continue' : 'Next'}
        disabled={!answers[current?.id]}
      />
    </>
  );
}

function ReviewScreen({ answers, sections, onEdit, onNext, onBack }) {
  return (
    <div  style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }} >
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 24px" }} >
      {/* <CardBody> */}
      <div className={styles.screenStepBadge} >
        <span className={styles.screenStepNum}>📋</span> Review
      </div>
      <h2 className={`ava-text-subheading ${styles.screenTitle}`}>Review your answers</h2>
      <p className={styles.screenSubtitle}>You can edit your responses before submitting</p>
      <div className={styles.reviewList}>
        {sections.map((sec) => {
          const vals = sec.fields.map((f) => answers[f]).filter(Boolean);
          return (
            <ReviewRow
              key={sec.id}
              icon={sec.icon}
              label={sec.label}
              value={vals.length ? vals.join(' · ') : '—'}
              onEdit={() => onEdit(sec.id)}
            />
          );
        })}
      </div>
      </div>
      {/* </CardBody> */}
      <CardFooter onBack={onBack} onNext={onNext} nextLabel="Show my result" />
    </div>
  );

// return (
//   <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>

//     <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 24px" }}>
//       <div className={styles.screenStepBadge}>
//         <span className={styles.screenStepNum}>📋</span> Review
//       </div>
//       <h2 className={`ava-text-subheading ${styles.screenTitle}`}>Review your answers</h2>
//       <p className={styles.screenSubtitle}>You can edit your responses before submitting</p>
//       <div className={styles.reviewList}>
//         {sections.map((sec) => {
//           const vals = sec.fields.map((f) => answers[f]).filter(Boolean);
//           return (
//             <ReviewRow
//               key={sec.id}
//               icon={sec.icon}
//               label={sec.label}
//               value={vals.length ? vals.join(' · ') : '—'}
//               onEdit={() => onEdit(sec.id)}
//             />
//           );
//         })}
//       </div>
//     </div>

//     <CardFooter onBack={onBack} onNext={onNext} nextLabel="Show my result" />
//   </div>
// );
}

function ResultScreen({ answers, onRestart }) {
  const pct = computeMenopauseScore(answers);
  const risk = getMenopauseRisk(pct);
  const conf = Math.min(74 + Math.round(pct * 0.11), 93);

  return (
    <div className={styles.cardBodyFull}>
      <h2 className={`ava-text-subheading ${styles.screenTitle}`}>Your Menopause Symptom Result</h2>
      <p className={styles.resultDisclaimer}>This is not a medical diagnosis.</p>

      <ResultGauge
        pct={pct}
        riskLevel={risk.level}
        riskColor={risk.color}
        riskBg={risk.bg}
        riskBorder={risk.border}
        riskEmoji={risk.emoji}
        riskDesc={risk.desc}
      />
      <ConfidenceBar pct={conf} color={risk.color} />

      <div className={styles.resultMeansCard}>
        <p className={styles.resultSectionTitle}>What this means</p>
        <p className={styles.resultMeansText}>{risk.means}</p>
      </div>

      <p className={styles.resultSectionTitle}>Recommended for you</p>
      <NextActionList actions={risk.actions} />

      <button className={styles.resultPrimaryBtn} onClick={onRestart}>
        Start a new screening <ChevronRight />
      </button>
      <button className={styles.resultSecondaryBtn}>Back to dashboard</button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ORCHESTRATOR
// ══════════════════════════════════════════════════════════════════════════════
const QUESTION_SCREEN_IDS = MENOPAUSE_QUESTION_SCREENS.map((s) => s.id);

export default function MenopauseRiskScreening() {
  const [flowIndex, setFlowIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [concerns, setConcerns] = useState([]);
const qIdxMapRef = useRef({});
  const currentId = MENOPAUSE_FLOW[flowIndex];
  // const goNext = () => setFlowIndex((i) => Math.min(i + 1, MENOPAUSE_FLOW.length - 1));
  const goNext = () => {
  setFlowIndex((i) => {
    const nextIndex = Math.min(i + 1, MENOPAUSE_FLOW.length - 1);
    const nextId = MENOPAUSE_FLOW[nextIndex];
    // Reset the next screen's question index so it always starts from Q1
    if (qIdxMapRef.current[nextId] !== undefined) {
      qIdxMapRef.current[nextId] = 0;
    }
    return nextIndex;
  });
};
  const goBack = () => setFlowIndex((i) => Math.max(i - 1, 0));

  const handleAnswer = (id, val) => setAnswers((prev) => ({ ...prev, [id]: val }));
  const handleRestart = () => { setFlowIndex(0); setAnswers({}); setConcerns([]); };
  // const handleEdit = (sectionId) => {
  //   const idx = MENOPAUSE_FLOW.indexOf(sectionId);
  //   if (idx !== -1) setFlowIndex(idx);
  // };
const handleEdit = (sectionId) => {
  const idx = MENOPAUSE_FLOW.indexOf(sectionId);
  if (idx !== -1) {
    qIdxMapRef.current[sectionId] = 0; // ← reset to first question
    setFlowIndex(idx);
  }
};
  const sidebarIndex = MENOPAUSE_STEPS.findIndex((s) => s.id === currentId);
  const qScreenIdx = QUESTION_SCREEN_IDS.indexOf(currentId);
  const isQuestion = qScreenIdx !== -1;
  const showSidebar = !['intro', 'consent', 'result'].includes(currentId);
  const content = {
    "title": "Risk Screening Personalization",
    "description": "Use my answers to personalize my health risk screening.",
    "pageSubtitle": "Let's find or create your profile so we can personalize your risk screening.",
  }

  return (
    <AppShell accent="accentPink">
      <Card>
        {currentId !== 'intro'
          ? <CardHeader
            stepLabel={currentId !== 'result' ? MENOPAUSE_STEPS.find((s) => s.id === currentId)?.label : null}
            onExit={currentId !== 'result' ? handleRestart : undefined}
          />
          : <div style={{ padding: '18px 24px 0' }}><Logo /></div>
        }

        {/* {currentId === 'intro'    && <IntroScreen    config={MENOPAUSE_INTRO}    onStart={goNext} />} */}
        {currentId === 'intro' && (
          <PregnancyWelcomeWeb
            onNext={goNext}
            eyebrow="Menopause Health"
            headlineAccent="menopause journey"
            bodyText="This screening helps identify where you are in your menopause transition and connects you with the support and care that fits your stage."
          // illustration={<MenopauseWomanSVG />}
          // onNext={handleNext}
          />
        )}
        {currentId === 'consent' &&
          <div style={{ padding: "2rem" }}>
            <ConsentScreen
              // config={MENOPAUSE_CONSENT}
              config={RISK_SCREENING_CONSENT}
              // styles={cardstyles}
              onNext={goNext} onBack={goBack}
              content={content}

            />
          </div>}
        {currentId === 'concerns' && (
          <ConcernsScreen
            config={MENOPAUSE_CONCERNS}
            selected={concerns}
            onChange={setConcerns}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {isQuestion && (
          <div className={styles.cardSep}>
            <div className={styles.cardSepLeft}>
              <QuestionScreen
                key={currentId}
                screenConfig={MENOPAUSE_QUESTION_SCREENS[qScreenIdx]}
                answers={answers}
                onChange={handleAnswer}
                onNext={goNext}
                onBack={goBack}
                questionIndex={qScreenIdx}
                totalQuestions={QUESTION_SCREEN_IDS.length}
                qIdxMap={qIdxMapRef}
              />
            </div>
            {showSidebar && <Sidebar steps={MENOPAUSE_STEPS} currentStep={sidebarIndex} />}
          </div>
        )}
        {currentId === 'review' && (
          <ReviewScreen
            answers={answers}
            sections={MENOPAUSE_REVIEW_SECTIONS}
            onEdit={handleEdit}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {currentId === 'result' && (
          <ResultScreen answers={answers} onRestart={handleRestart} />
        )}
      </Card>


    </AppShell>
  );
}
